import { type FireAnalyticsEvent } from '../../../common/types';
import { is3pProductKeyForAnalytics } from '../../constants/is3p-product-key-for-analytics';
import type { ConnectorSource } from '../../constants/schemas/3p-config';
import type { ResultClickedCallbackParams } from '../../constants/types';
import { type CommonAttributes, type NonPrivacySafeAttributes } from './types';

/** Metadata for when the result is interacted with, e.g. clicked or right clicked */
export type AdvancedSearchResultInteractedMetadata = {
	id: string;
	entityType: string;
	contentType?: string | null;
	contentId: string;
	spaceId: string | null;
	containerId: string | null;
	product?: string;
	is3PLink?: boolean;
	is3PSmartLink?: boolean;
	isSideBySideEnabled?: boolean;
	datasourceId?: string;
	connectorType?: ConnectorSource;
	fromLinksSeeAll?: boolean;
	thirdPartyId?: string;
	/** How many sites the search covered. Undefined when the search is not site-scoped. */
	selectedSitesCount?: number;
	/** Whether the result belongs to the current site. Undefined when not attributable. */
	isPrimarySite?: boolean;
	/** How many sites are available to the user. Undefined until site metadata has loaded. */
	availableSitesCount?: number;
};

/** Result-event data known before the user interacts. */
export type ResultEventAttributes = {
	metadata: AdvancedSearchResultInteractedMetadata;
	/** The index in which the result was shown to the user. */
	position?: number;
	socialSignal?: string;
	isImpersonated?: boolean;
	is3PClickIngestionEnabled?: boolean;
	intentDetectionTriggered?: boolean;
};

interface ResultInteractedProps {
	resultEventAttributes: ResultEventAttributes;
	interactionAttributes: ResultClickedCallbackParams;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
}

export const onAdvancedSearchResultLinkClicked = ({
	resultEventAttributes: {
		metadata,
		position,
		socialSignal,
		isImpersonated = false,
		is3PClickIngestionEnabled,
		intentDetectionTriggered,
	},
	interactionAttributes: {
		fromLinksSeeAll,
		sublistPosition,
		provider,
		linkDocId,
		linkDocContentType,
		fanoutProduct,
		fanoutLinkCategory,
		hasRelatedLinks,
		thirdPartyId,
		isAuxClick = false,
		isSummarizeClick = false,
	},
	commonAttributes,
	nonPrivacySafeAttributes,
}: ResultInteractedProps): FireAnalyticsEvent => {
	const { product: metadataProduct, ...restMetadata } = metadata;
	const product = fanoutProduct || metadataProduct;

	return {
		eventType: 'ui',
		action: 'clicked',
		actionSubject: 'advancedSearchResultLink',
		attributes: {
			additionalAttributes: {
				position,
				metadata: {
					...restMetadata,
					product,
					...(fanoutProduct && { parentProduct: metadataProduct }),
					...(fanoutLinkCategory && { fanoutLinkCategory }),
					is3PLink: is3pProductKeyForAnalytics(product),
					fromLinksSeeAll,
					sublistPosition,
					provider,
					linkDocId,
					linkDocContentType,
					hasRelatedLinks,
					thirdPartyId,
				},
				isImpersonated,
				socialSignal,
				is3PClickIngestionEnabled,
				isAuxClick,
				isSummarizeClick,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
	};
};

export const onAdvancedSearchResultLinkContextMenuClicked = ({
	resultEventAttributes: {
		metadata,
		position,
		socialSignal,
		isImpersonated = false,
		is3PClickIngestionEnabled,
		intentDetectionTriggered,
	},
	interactionAttributes: {
		sublistPosition,
		linkDocId,
		linkDocContentType,
		fanoutProduct,
		fanoutLinkCategory,
		thirdPartyId,
	},
	commonAttributes,
	nonPrivacySafeAttributes,
}: ResultInteractedProps): FireAnalyticsEvent => {
	const { product: metadataProduct, ...restMetadata } = metadata;
	const product = fanoutProduct || metadataProduct;

	return {
		eventType: 'ui',
		action: 'contextMenuClicked',
		actionSubject: 'advancedSearchResultLink',
		attributes: {
			additionalAttributes: {
				position,
				metadata: {
					...restMetadata,
					product,
					...(fanoutProduct && { parentProduct: metadataProduct }),
					...(fanoutLinkCategory && { fanoutLinkCategory }),
					is3PLink: is3pProductKeyForAnalytics(product),
					sublistPosition,
					linkDocId,
					linkDocContentType,
					thirdPartyId,
				},
				isImpersonated,
				socialSignal,
				is3PClickIngestionEnabled,
				...(intentDetectionTriggered !== undefined ? { intentDetectionTriggered } : {}),
			},
			commonAttributes,
		},
		nonPrivacySafeAttributes,
	};
};
